import React, { useState, useEffect, useCallback } from 'react';
import { getAuth, signOut } from 'firebase/auth';
import { collection, query, where, getDocs, getDoc, doc } from 'firebase/firestore';
import { Calendar, User, Clock, MapPin, LogOut, Home, ChevronRight, Grid, Layout, Shield, Heart, Sun, Moon, Monitor, MessageSquare, Bell, X, CreditCard, FileText, ClipboardList } from 'lucide-react';
import ClientLayout from './ClientLayout';
import ClientCalendarView from './ClientCalendarView';
import ClientPersonalInfo from './ClientPersonalInfo';
import ClientMedicalInfo from './ClientMedicalInfo';
import ClientMessaging from './ClientMessaging';
import ClientBillingTab from './ClientBillingTab';
import ClientMobileNav from './ClientMobileNav';
import ClientSelfAssessment from './ClientSelfAssessment';
import { StatCard, Avatar, Badge } from './ClientComponents';
import { useTheme } from '../contexts/ThemeContext';
import { ClientMessagingProvider, useClientMessaging } from '../contexts/ClientMessagingContext';
import clientPaymentService from '../services/clientPaymentService';
import { getAssessmentNotificationMessage, isQuarterlyAssessmentNeeded } from '../services/profileEnrichmentService';
import { BeneficiaryDocumentsManager } from './beneficiaryDocuments';
import '../ClientDashboard.css';

// Composant de notification pour l'auto-évaluation trimestrielle
const AssessmentNotification = ({ message, onClick, onDismiss }) => {
  const isDarkMode = document.documentElement.classList.contains('dark');

  return (
    <div
      className={`fixed top-4 right-4 z-50 animate-slide-in-up`}
      style={{
        animation: 'slideInUp 0.3s ease-out'
      }}
    >
      <div
        className={`flex items-center gap-3 px-4 py-3 rounded-lg shadow-lg border cursor-pointer max-w-md ${isDarkMode
          ? 'bg-orange-900 border-orange-700 text-white'
          : 'bg-orange-50 border-orange-200 text-orange-900'
          }`}
        onClick={onClick}
      >
        <div className={`p-2 rounded-full ${isDarkMode ? 'bg-orange-800' : 'bg-orange-100'}`}>
          <ClipboardList size={20} className={isDarkMode ? 'text-orange-200' : 'text-orange-600'} />
        </div>
        <div className="flex-1">
          <p className={`font-semibold text-sm ${isDarkMode ? 'text-white' : 'text-orange-900'}`}>
            Mise à jour de votre évaluation
          </p>
          <p className={`text-xs ${isDarkMode ? 'text-orange-200' : 'text-orange-700'}`}>
            {message}
          </p>
        </div>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDismiss();
          }}
          className={`p-1 rounded-full hover:bg-opacity-20 hover:bg-white transition-colors ${isDarkMode ? 'text-orange-200 hover:text-white' : 'text-orange-600 hover:text-orange-800'
            }`}
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
};

// Composant de notification pour les messages non lus
const MessageNotification = ({ unreadCount, onClick, onDismiss }) => {
  const isDarkMode = document.documentElement.classList.contains('dark');

  if (unreadCount === 0) return null;

  return (
    <div
      className={`fixed bottom-4 right-4 z-50 animate-slide-in-up`}
      style={{
        animation: 'slideInUp 0.3s ease-out'
      }}
    >
      <div
        className={`flex items-center gap-3 px-4 py-3 rounded-lg shadow-lg border cursor-pointer ${isDarkMode
          ? 'bg-blue-900 border-blue-700 text-white'
          : 'bg-blue-50 border-blue-200 text-blue-900'
          }`}
        onClick={onClick}
      >
        <div className={`p-2 rounded-full ${isDarkMode ? 'bg-blue-800' : 'bg-blue-100'}`}>
          <Bell size={20} className={isDarkMode ? 'text-blue-200' : 'text-blue-600'} />
        </div>
        <div className="flex-1">
          <p className={`font-semibold text-sm ${isDarkMode ? 'text-white' : 'text-blue-900'}`}>
            {unreadCount} nouveau{unreadCount > 1 ? 'x' : ''} message{unreadCount > 1 ? 's' : ''}
          </p>
          <p className={`text-xs ${isDarkMode ? 'text-blue-200' : 'text-blue-700'}`}>
            Cliquez pour voir
          </p>
        </div>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDismiss();
          }}
          className={`p-1 rounded-full hover:bg-opacity-20 hover:bg-white transition-colors ${isDarkMode ? 'text-blue-200 hover:text-white' : 'text-blue-600 hover:text-blue-800'
            }`}
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
};

// Composant interne qui a accès au contexte de messagerie
function ClientHomeContent({ db, client }) {
  const { unreadCounts } = useClientMessaging();
  const [todayAppointments, setTodayAppointments] = useState([]);
  const [upcomingAppointments, setUpcomingAppointments] = useState([]);
  const [intervenants, setIntervenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);

  // Détection du mode mobile
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const [activeTab, setActiveTab] = useState('dashboard'); // 'dashboard' or 'calendar'
  const [programName, setProgramName] = useState('Agentime');
  const { theme, changeTheme } = useTheme();
  const [showThemeDropdown, setShowThemeDropdown] = useState(false);
  const [themeChangeFeedback, setThemeChangeFeedback] = useState('');
  const [showNotification, setShowNotification] = useState(true);
  const [previousUnreadCount, setPreviousUnreadCount] = useState(0);
  const [invoicesStats, setInvoicesStats] = useState({
    pending: 0,
    totalDue: 0,
    overdue: 0
  });
  const [assessmentNotification, setAssessmentNotification] = useState(null);
  const [showAssessmentNotification, setShowAssessmentNotification] = useState(false);

  // Gérer l'affichage de la notification quand le nombre de messages non lus change
  useEffect(() => {
    const totalUnread = unreadCounts.client + unreadCounts.group;

    // Si le nombre de messages non lus augmente, afficher la notification
    if (totalUnread > previousUnreadCount && totalUnread > 0) {
      setShowNotification(true);

      // Auto-masquer la notification après 10 secondes si pas dans l'onglet messagerie
      if (activeTab !== 'messaging') {
        const timer = setTimeout(() => {
          setShowNotification(false);
        }, 10000);

        return () => clearTimeout(timer);
      }
    }

    // Si on est dans l'onglet messagerie, masquer la notification
    if (activeTab === 'messaging') {
      setShowNotification(false);
    }

    setPreviousUnreadCount(totalUnread);
  }, [unreadCounts, activeTab, previousUnreadCount]);

  // Fonction pour gérer le clic sur la notification
  const handleNotificationClick = () => {
    setActiveTab('messaging');
    setShowNotification(false);
  };

  // Fonction pour fermer la notification
  const handleDismissNotification = () => {
    setShowNotification(false);
  };

  // Fonction pour gérer le clic sur la notification d'auto-évaluation
  const handleAssessmentNotificationClick = () => {
    setActiveTab('self-assessment');
    setShowAssessmentNotification(false);
  };

  // Fonction pour fermer la notification d'auto-évaluation
  const handleDismissAssessmentNotification = () => {
    setShowAssessmentNotification(false);
  };

  // Fermer le dropdown quand on clique en dehors
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showThemeDropdown && !event.target.closest('.theme-selector')) {
        setShowThemeDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showThemeDropdown]);

  // Fonction pour changer le thème avec feedback
  const handleThemeChange = async (newTheme) => {
    try {
      await changeTheme(newTheme);
      setShowThemeDropdown(false);
      setThemeChangeFeedback(`Thème changé en ${newTheme === 'light' ? 'clair' : newTheme === 'dark' ? 'sombre' : 'système'}`);

      // Effacer le feedback après 3 secondes
      setTimeout(() => {
        setThemeChangeFeedback('');
      }, 3000);
    } catch (error) {
      console.error('Erreur lors du changement de thème:', error);
      setThemeChangeFeedback('Erreur lors du changement de thème');
      setTimeout(() => {
        setThemeChangeFeedback('');
      }, 3000);
    }
  };

  const fetchApplicationSettings = useCallback(async () => {
    if (!db) return;
    try {
      const settingsDoc = await getDoc(doc(db, 'settings', 'general'));
      if (settingsDoc.exists()) {
        const settings = settingsDoc.data();
        if (settings.organizationName) {
          setProgramName(settings.organizationName);
        }
      }
    } catch (error) {
      console.error("Erreur lors de la récupération des paramètres de l'application:", error);
    }
  }, [db]);

  // Charger les statistiques des factures
  const loadInvoicesStats = useCallback(async () => {
    if (!client?.id) return;

    try {
      // Utiliser le même service que ClientBillingTab
      const invoices = await clientPaymentService.getClientInvoices(client.id);

      // Calculer les factures en attente et le montant total dû
      const pendingInvoices = invoices.filter(inv =>
        inv.status === 'pending' ||
        inv.status === 'sent' ||
        inv.status === 'overdue'
      );

      // Compter spécifiquement les factures en retard
      const overdueInvoices = invoices.filter(inv => inv.status === 'overdue');

      const totalDue = pendingInvoices.reduce((sum, inv) => sum + (inv.amount || 0), 0);

      setInvoicesStats({
        pending: pendingInvoices.length,
        totalDue: totalDue / 100, // Convertir de centimes en euros
        overdue: overdueInvoices.length // Nombre de factures en retard
      });
    } catch (error) {
      console.error('Erreur lors du chargement des statistiques de factures:', error);
    }
  }, [client]);

  // Vérifier si une auto-évaluation trimestrielle est nécessaire
  const checkAssessmentNotification = useCallback(() => {
    if (client) {
      const notificationMessage = getAssessmentNotificationMessage(client);
      if (notificationMessage) {
        setAssessmentNotification(notificationMessage);
        setShowAssessmentNotification(true);
      } else {
        setShowAssessmentNotification(false);
      }
    }
  }, [client]);

  useEffect(() => {
    fetchApplicationSettings();
    loadAppointments();
    loadInvoicesStats();
    checkAssessmentNotification();
  }, [client, fetchApplicationSettings, loadInvoicesStats, checkAssessmentNotification]);

  const loadAppointments = async () => {
    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);

      const nextWeek = new Date(today);
      nextWeek.setDate(nextWeek.getDate() + 7);

      // Récupérer les rendez-vous du client
      const appointmentsRef = collection(db, 'appointments');
      const q = query(
        appointmentsRef,
        where('clientId', '==', client.id),
        where('date', '>=', today)
      );

      const querySnapshot = await getDocs(q);
      const appointments = [];

      querySnapshot.forEach((doc) => {
        const appointment = {
          id: doc.id,
          ...doc.data()
        };
        appointments.push(appointment);
      });

      // Trier les rendez-vous
      const todayApps = appointments.filter(app => {
        const appDate = app.date.toDate();
        return appDate.toDateString() === today.toDateString();
      }).sort((a, b) => a.startTime.localeCompare(b.startTime));

      const upcomingApps = appointments.filter(app => {
        const appDate = app.date.toDate();
        return appDate > tomorrow && appDate <= nextWeek;
      }).sort((a, b) => a.date - b.date);

      // Récupérer les noms des collaborateurs uniques
      const employeeIds = [...new Set(appointments.map(app => app.employeeId))];
      const intervenantsList = await getIntervenantsDetails(employeeIds);

      setTodayAppointments(todayApps);
      setUpcomingAppointments(upcomingApps.slice(0, 5)); // Limiter à 5 prochains
      setIntervenants(intervenantsList);
    } catch (error) {
      console.error('Erreur lors du chargement des rendez-vous:', error);
    } finally {
      setLoading(false);
    }
  };

  const getIntervenantsDetails = async (employeeIds) => {
    try {
      if (employeeIds.length === 0) return [];

      // Récupérer tous les employés sans filtre isArchived
      const employeesRef = collection(db, 'employees');
      const querySnapshot = await getDocs(employeesRef);
      const allEmployees = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Filtrer côté client pour inclure les employés où isArchived est false ou absent
      const activeEmployees = allEmployees.filter(employee => {
        // Inclure si isArchived est false ou absent/undefined
        const isNotArchived = employee.isArchived !== true;
        const isActive = employee.isActive !== false;
        return isNotArchived && isActive;
      });

      // Filtrer pour ne garder que les employés demandés
      const intervenantsDetails = activeEmployees.filter(employee =>
        employeeIds.includes(employee.id)
      );

      console.log('🔍 Intervenants récupérés dans ClientHome:', {
        totalEmployés: allEmployees.length,
        employésActifs: activeEmployees.length,
        intervenantsDemandés: employeeIds.length,
        intervenantsTrouvés: intervenantsDetails.length,
        détails: intervenantsDetails.map(emp => ({
          id: emp.id,
          nom: emp.name || `${emp.firstName} ${emp.lastName}`,
          isArchived: emp.isArchived,
          isActive: emp.isActive
        }))
      });

      return intervenantsDetails;
    } catch (error) {
      console.error('Erreur lors de la récupération des intervenants:', error);
      return [];
    }
  };

  const formatTime = (time) => {
    return time.substring(0, 5); // Format HH:MM
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('fr-FR', {
      weekday: 'short',
      day: 'numeric',
      month: 'short'
    });
  };

  if (loading) {
    return (
      <ClientLayout db={db}>
        <div className="flex justify-center items-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600 dark:text-gray-400">Chargement...</p>
          </div>
        </div>
      </ClientLayout>
    );
  }

  const totalUnread = unreadCounts.client + unreadCounts.group;

  const NavItems = [
    { id: 'dashboard', icon: Grid, label: 'Tableau de bord' },
    { id: 'calendar', icon: Calendar, label: 'Planning complet' },
    { id: 'personal-info', icon: Shield, label: 'Mes informations' },
    { id: 'medical-info', icon: Heart, label: 'Informations médicales' },
    { id: 'messaging', icon: MessageSquare, label: 'Messagerie', badge: totalUnread },
    { id: 'billing', icon: CreditCard, label: 'Mes factures' },
    { id: 'documents', icon: FileText, label: 'Mes documents' },
    { id: 'self-assessment', icon: ClipboardList, label: 'Mon évaluation' },
  ];

  return (
    <ClientLayout db={db}>
      <div className="client-dashboard-layout">
        {/* Sidebar Desktop */}
        <aside className="sidebar-desktop">
          <div className="sidebar-logo">
            <span className="text-blue-600">Agen</span>Time
          </div>

          <nav className="sidebar-nav">
            {NavItems.map((item) => (
              <button
                key={item.id}
                onClick={() => setActiveTab(item.id)}
                className={`sidebar-link ${activeTab === item.id ? 'active' : ''}`}
              >
                <item.icon size={20} className="mr-3" />
                <span className="flex-1">{item.label}</span>
                {item.badge > 0 && (
                  <span className="bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-bold">
                    {item.badge > 9 ? '9+' : item.badge}
                  </span>
                )}
              </button>
            ))}
          </nav>

          <div className="mt-auto pt-6 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={() => handleLogout()}
              className="sidebar-link text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
            >
              <LogOut size={20} className="mr-3" />
              Déconnexion
            </button>
          </div>
        </aside>

        <div className={`client-dashboard-container ${activeTab === 'messaging' ? 'overflow-hidden !h-screen !p-0' : ''}`}>
          {/* Header Premium - Hidden on mobile messaging to maximize space */}
          <header className={`client-header ${activeTab === 'messaging' ? 'hidden' : isMobile && activeTab === 'messaging' ? 'hidden' : 'block'}`}>
            <div className="flex items-center justify-between w-full">
              <div className="flex items-center gap-4">
                <Avatar
                  fallback={`${client.firstName?.[0] || ''}${client.lastName?.[0] || ''}`}
                  color={client.color || '#3B82F6'}
                  size="lg"
                  status="online"
                />
                <div>
                  <h1 className="client-welcome">
                    Bonjour, {client.firstName} 👋
                  </h1>
                  <p className="client-subtitle">{programName} - Espace {activeTab === 'dashboard' ? 'personnel' : NavItems.find(i => i.id === activeTab)?.label}</p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                {/* Sélecteur de thème minimalist */}
                <div className="relative theme-selector">
                  <button
                    onClick={() => setShowThemeDropdown(!showThemeDropdown)}
                    className="p-2 rounded-xl bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 transition-all"
                  >
                    {theme === 'light' && <Sun size={20} />}
                    {theme === 'dark' && <Moon size={20} />}
                    {theme === 'system' && <Monitor size={20} />}
                  </button>

                  {showThemeDropdown && (
                    <div className="absolute right-0 mt-2 w-40 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl z-50 overflow-hidden">
                      {['light', 'dark', 'system'].map((t) => (
                        <button
                          key={t}
                          onClick={() => handleThemeChange(t)}
                          className="flex items-center gap-3 w-full px-4 py-3 text-sm hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
                        >
                          {t === 'light' && <Sun size={16} />}
                          {t === 'dark' && <Moon size={16} />}
                          {t === 'system' && <Monitor size={16} />}
                          {t === 'light' ? 'Clair' : t === 'dark' ? 'Sombre' : 'Système'}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <div className="hidden sm:flex flex-col items-end text-right">
                  <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">Votre adresse</span>
                  <span className="text-sm font-medium text-gray-600 dark:text-gray-300 max-w-[200px] truncate">
                    {typeof client.address === 'string'
                      ? client.address
                      : `${client.address?.street || ''} ${client.addressNumber || ''}`.trim()
                    }
                  </span>
                </div>
              </div>
            </div>
          </header>

          {/* Navigation Tabs - Only for medium screens between mobile and large desktop - Hidden on mobile messaging */}
          <div className={`client-tabs ${isMobile && activeTab === 'messaging' ? 'hidden' : ''}`}>
            <div className="flex space-x-2">
              {NavItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => setActiveTab(item.id)}
                  className={`tab-button ${activeTab === item.id ? 'active' : ''}`}
                >
                  <item.icon size={18} className="mr-2" />
                  {item.label}
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content Area */}
          <div className={`client-dashboard-content ${['messaging', 'calendar', 'documents'].includes(activeTab) ? 'overflow-hidden h-full flex flex-col' : ''}`}>
            {activeTab === 'dashboard' ? (
              <>
                {/* Stats Grid - Version modernisée avec nouveaux composants */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                  <StatCard
                    icon={Calendar}
                    label="RDV aujourd'hui"
                    value={todayAppointments.length}
                    color="blue"
                  />

                  <StatCard
                    icon={Clock}
                    label="RDV à venir"
                    value={upcomingAppointments.length}
                    color="green"
                  />

                  <StatCard
                    icon={User}
                    label="Intervenants"
                    value={new Set(todayAppointments.map(a => a.employeeId)).size}
                    color="purple"
                  />

                  <StatCard
                    icon={CreditCard}
                    label="Factures en attente"
                    value={invoicesStats.pending}
                    color={invoicesStats.overdue > 0 ? 'red' : 'orange'}
                    subValue={invoicesStats.totalDue > 0 ? `${invoicesStats.totalDue.toFixed(2)} €` : null}
                    alert={invoicesStats.overdue > 0 ? `${invoicesStats.overdue} en retard` : null}
                    onClick={() => setActiveTab('billing')}
                  />
                </div>


                {/* Today's Appointments */}
                <div className="content-section">
                  <div className="section-header">
                    <Calendar className="section-icon" size={24} />
                    <h2 className="section-title">Aujourd'hui</h2>
                  </div>

                  {todayAppointments.length === 0 ? (
                    <div className="empty-state">
                      <div className="empty-state-icon">📅</div>
                      <p>Aucun rendez-vous prévu aujourd'hui</p>
                    </div>
                  ) : (
                    <div className="appointment-list">
                      {todayAppointments.map((appointment) => {
                        const intervenant = intervenants.find(i => i.id === appointment.employeeId);
                        const employeeColor = intervenant ? intervenant.color || '#667eea' : '#667eea';

                        return (
                          <div
                            key={appointment.id}
                            className="appointment-card"
                            style={{ '--employee-color': employeeColor }}
                          >
                            <div className="flex items-center justify-between">
                              <div
                                className="p-3 rounded-lg mr-4 flex items-center justify-center"
                                style={{ backgroundColor: employeeColor }}
                              >
                                <Clock className="text-white" size={20} />
                              </div>
                              <div>
                                <p className="appointment-time">
                                  {formatTime(appointment.startTime)} - {formatTime(appointment.endTime)}
                                </p>
                                <p className="appointment-intervenant">
                                  {appointment.employeeId ?
                                    intervenants.find(i => i.id === appointment.employeeId)?.name ||
                                    `${intervenants.find(i => i.id === appointment.employeeId)?.firstName || ''} ${intervenants.find(i => i.id === appointment.employeeId)?.lastName || ''}`.trim() ||
                                    'Intervenant non spécifié'
                                    : 'Intervenant non spécifié'}
                                </p>
                              </div>
                            </div>
                            <div className="appointment-service">
                              <p className="service-label">Service</p>
                              <p className="service-name">{appointment.serviceType}</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Intervenants Section */}
                {intervenants.length > 0 && (
                  <div className="content-section">
                    <div className="section-header">
                      <User className="section-icon" size={24} />
                      <h2 className="section-title">Vos intervenants</h2>
                    </div>

                    <div className="intervenants-grid">
                      {intervenants.map((intervenant) => (
                        <div key={intervenant.id} className="intervenant-card">
                          <div className="flex items-center mb-4">
                            <div
                              className="intervenant-avatar"
                              style={{ backgroundColor: intervenant.color || '#8b5cf6' }}
                            >
                              {intervenant.firstName?.[0]}{intervenant.lastName?.[0]}
                            </div>
                            <div>
                              <h3 className="intervenant-name">
                                {intervenant.firstName} {intervenant.lastName}
                              </h3>
                              <p className="intervenant-position">{intervenant.position}</p>
                            </div>
                          </div>

                          <div className="intervenant-details">
                            {intervenant.phone && (
                              <div className="detail-item">
                                <span className="mr-2">📞</span>
                                {intervenant.phone}
                              </div>
                            )}
                            {intervenant.specialite && (
                              <div className="detail-item">
                                <span className="mr-2">⭐</span>
                                Spécialité: {intervenant.specialite}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Upcoming Appointments */}
                <div className="content-section">
                  <div className="section-header">
                    <Calendar className="section-icon" size={24} />
                    <h2 className="section-title">Prochains rendez-vous</h2>
                  </div>

                  {upcomingAppointments.length === 0 ? (
                    <div className="empty-state">
                      <div className="empty-state-icon">📅</div>
                      <p>Aucun rendez-vous à venir</p>
                    </div>
                  ) : (
                    <div className="appointment-list">
                      {upcomingAppointments.map((appointment) => {
                        const intervenant = intervenants.find(i => i.id === appointment.employeeId);
                        const employeeColor = intervenant ? intervenant.color || '#10b981' : '#10b981'; // green default

                        return (
                          <div
                            key={appointment.id}
                            className="appointment-card"
                            style={{ '--employee-color': employeeColor }}
                          >
                            <div className="flex items-center justify-between">
                              <div
                                className="flex items-center justify-center p-2 rounded-lg mr-4"
                                style={{ backgroundColor: employeeColor }}
                              >
                                <Calendar className="text-white" size={18} />
                              </div>
                              <div>
                                <p className="appointment-time">
                                  {formatDate(appointment.date.toDate())}
                                </p>
                                <p className="appointment-intervenant">
                                  {formatTime(appointment.startTime)} - {formatTime(appointment.endTime)} • {appointment.employeeId ?
                                    intervenants.find(i => i.id === appointment.employeeId)?.name ||
                                    `${intervenants.find(i => i.id === appointment.employeeId)?.firstName || ''} ${intervenants.find(i => i.id === appointment.employeeId)?.lastName || ''}`.trim() ||
                                    'Intervenant non spécifié'
                                    : 'Intervenant non spécifié'}
                                </p>
                              </div>
                            </div>
                            <ChevronRight className="text-gray-400" size={20} />
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </>
            ) : activeTab === 'calendar' ? (
              <ClientCalendarView db={db} client={client} />
            ) : activeTab === 'personal-info' ? (
              <ClientPersonalInfo db={db} client={client} />
            ) : activeTab === 'medical-info' ? (
              <ClientMedicalInfo db={db} client={client} />
            ) : activeTab === 'messaging' ? (
              <ClientMessaging db={db} client={client} />
            ) : activeTab === 'billing' ? (
              <ClientBillingTab db={db} client={client} currentUser={getAuth().currentUser} />
            ) : activeTab === 'documents' ? (
              <div className="h-full">
                <BeneficiaryDocumentsManager
                  db={db}
                  user={getAuth().currentUser}
                  beneficiaryId={client.id}
                  isClientView={true}
                />
              </div>
            ) : activeTab === 'self-assessment' ? (
              <div className="content-section">
                <ClientSelfAssessment db={db} client={client} />
              </div>
            ) : null}
          </div>
        </div>

        {/* Notification d'auto-évaluation trimestrielle */}
        {showAssessmentNotification && assessmentNotification && activeTab !== 'self-assessment' && (
          <AssessmentNotification
            message={assessmentNotification}
            onClick={handleAssessmentNotificationClick}
            onDismiss={handleDismissAssessmentNotification}
          />
        )}

        {/* Notification de nouveaux messages */}
        {showNotification && totalUnread > 0 && activeTab !== 'messaging' && (
          <MessageNotification
            unreadCount={totalUnread}
            onClick={handleNotificationClick}
            onDismiss={handleDismissNotification}
          />
        )}

        {/* Navigation mobile bottom bar */}
        <ClientMobileNav
          activeTab={activeTab}
          onTabChange={setActiveTab}
          unreadCount={totalUnread}
          overdueInvoices={invoicesStats.overdue}
        />
      </div>
    </ClientLayout>
  );
}

// Wrapper component that provides ClientMessagingContext
const ClientHome = ({ db, client }) => {
  return (
    <ClientMessagingProvider db={db} client={client}>
      <ClientHomeContent db={db} client={client} />
    </ClientMessagingProvider>
  );
};

export default ClientHome;
